import { useEffect, useMemo, useState } from "react";
import {
  addAdminEventAttendee,
  createAdminEvent,
  createAdminEventTemplate,
  deleteAdminEvent,
  deleteAdminEventTemplate,
  getAdminEventTemplates,
  getAdminEvents,
  removeAdminEventAttendee,
  updateAdminEvent,
  updateAdminEventAttendee,
  updateAdminEventTemplate,
  uploadAdminEventImage,
} from "../../../api/adminEvents";

const toLocalInput = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

const fromLocalInput = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const DEFAULT_SELF_ADJUSTING_WEEKDAYS = [1, 2, 3, 4, 5];

const minutesToTime = (minutes, fallback = "18:00") => {
  const value = Number.parseInt(minutes, 10);
  if (!Number.isFinite(value)) return fallback;
  const bounded = Math.max(0, Math.min(1439, value));
  const hours = String(Math.floor(bounded / 60)).padStart(2, "0");
  const mins = String(bounded % 60).padStart(2, "0");
  return `${hours}:${mins}`;
};

const timeToMinutes = (value, fallback = 0) => {
  const [hoursRaw, minutesRaw] = String(value || "").split(":");
  const hours = Number.parseInt(hoursRaw, 10);
  const minutes = Number.parseInt(minutesRaw, 10);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return fallback;
  return Math.max(0, Math.min(1439, hours * 60 + minutes));
};

const mapScheduleToForm = (schedule) => ({
  selfAdjustingWeekdays:
    Array.isArray(schedule?.weekdays) && schedule.weekdays.length
      ? schedule.weekdays.map(Number).filter((day) => day >= 0 && day <= 6)
      : DEFAULT_SELF_ADJUSTING_WEEKDAYS,
  pickupStartTime: minutesToTime(schedule?.pickupStartMinutes, "18:00"),
  pickupEndTime: minutesToTime(schedule?.returnEndMinutes ?? schedule?.pickupEndMinutes, "22:00"),
  returnStartTime: minutesToTime(schedule?.pickupStartMinutes, "18:00"),
  returnEndTime: minutesToTime(schedule?.returnEndMinutes, "22:00"),
});

const buildSelfAdjustingSchedule = (form) =>
  form.isSelfAdjusting
    ? (() => {
        const windowStartMinutes = timeToMinutes(form.pickupStartTime, 18 * 60);
        const windowEndMinutes = timeToMinutes(form.pickupEndTime || form.returnEndTime, 22 * 60);
        return {
          weekdays: (form.selfAdjustingWeekdays || [])
            .map(Number)
            .filter((day) => day >= 0 && day <= 6),
          pickupStartMinutes: windowStartMinutes,
          pickupEndMinutes: windowEndMinutes,
          returnStartMinutes: windowStartMinutes,
          returnEndMinutes: windowEndMinutes,
        };
      })()
    : null;

const createEmptyForm = (locations = [], equipmentSnapshot = []) => {
  const start = new Date();
  start.setDate(start.getDate() + 7);
  start.setHours(18, 0, 0, 0);
  const end = new Date(start);
  end.setHours(start.getHours() + 2);
  const locationId = locations[0]?.id || "";
  const equipmentTypeId = getDefaultEquipmentTypeId(locations, equipmentSnapshot, locationId);

  return {
    title: "",
    description: "",
    descriptionEn: "",
    descriptionDe: "",
    includedText: "",
    includedTextEn: "",
    includedTextDe: "",
    locationId,
    startLocal: toLocalInput(start),
    endLocal: toLocalInput(end),
    capacity: "10",
    priceAmount: "24",
    equipmentTypeId,
    equipmentTypeIds: equipmentTypeId ? [equipmentTypeId] : [],
    equipmentQuantityPerAttendee: "1",
    imageUrl: "/assets/pos_main.jpg",
    eventLocationName: "",
    eventLocationAddress: "",
    mapUrl: "",
    isSelfAdjusting: false,
    isActive: false,
    repeatFrequency: "none",
    repeatCount: "1",
    ...mapScheduleToForm(null),
  };
};

const getLocationEquipmentIds = (locations = [], locationId) => {
  const location = locations.find((item) => item.id === locationId);
  if (!location || !Array.isArray(location.inventory)) return new Set();

  return new Set(
    location.inventory
      .filter((item) => {
        const quantity = Number.parseInt(item.quantity, 10) || 0;
        const maintenance = Number.parseInt(item.maintenanceQuantity, 10) || 0;
        const lost = Number.parseInt(item.lostQuantity, 10) || 0;
        return quantity - maintenance - lost > 0;
      })
      .map((item) => item.equipmentTypeId)
      .filter(Boolean)
  );
};

const getDefaultEquipmentTypeId = (locations = [], equipmentSnapshot = [], locationId) => {
  const locationEquipmentIds = getLocationEquipmentIds(locations, locationId);
  if (!locationEquipmentIds.size) return "";
  return equipmentSnapshot.find((item) => locationEquipmentIds.has(item.id))?.id || "";
};

const normalizeEquipmentSelection = ({
  locations = [],
  equipmentSnapshot = [],
  locationId,
  equipmentTypeId,
  equipmentTypeIds,
}) => {
  const locationEquipmentIds = getLocationEquipmentIds(locations, locationId);
  const defaultEquipmentTypeId = getDefaultEquipmentTypeId(locations, equipmentSnapshot, locationId);
  const nextEquipmentTypeId =
    equipmentTypeId && locationEquipmentIds.has(equipmentTypeId)
      ? equipmentTypeId
      : defaultEquipmentTypeId;
  const allowedIds = new Set(
    Array.isArray(equipmentTypeIds)
      ? equipmentTypeIds.filter((id) => locationEquipmentIds.has(id))
      : []
  );

  if (nextEquipmentTypeId) {
    allowedIds.add(nextEquipmentTypeId);
  }

  return {
    equipmentTypeId: nextEquipmentTypeId,
    equipmentTypeIds: [...allowedIds],
  };
};

const mapEventToForm = (event) => ({
  title: event.title || "",
  description: event.description || "",
  descriptionEn: event.descriptionEn || event.description || "",
  descriptionDe: event.descriptionDe || "",
  includedText: Array.isArray(event.included) ? event.included.join("\n") : "",
  includedTextEn: Array.isArray(event.includedEn) && event.includedEn.length
    ? event.includedEn.join("\n")
    : Array.isArray(event.included)
      ? event.included.join("\n")
      : "",
  includedTextDe: Array.isArray(event.includedDe) ? event.includedDe.join("\n") : "",
  locationId: event.locationId || "",
  startLocal: toLocalInput(event.startUtc),
  endLocal: toLocalInput(event.endUtc),
  capacity: String(event.capacity ?? ""),
  priceAmount: String(event.priceAmount ?? ""),
  equipmentTypeId: event.equipmentTypeId || "",
  equipmentTypeIds:
    Array.isArray(event.equipmentOptions) && event.equipmentOptions.length
      ? event.equipmentOptions.map((item) => item.equipmentTypeId).filter(Boolean)
      : event.equipmentTypeId
        ? [event.equipmentTypeId]
        : [],
  equipmentQuantityPerAttendee: String(event.equipmentQuantityPerAttendee ?? 1),
  imageUrl: event.imageUrl || "",
  eventLocationName: event.eventLocationName || "",
  eventLocationAddress: event.eventLocationAddress || "",
  mapUrl: event.mapUrl || "",
  isSelfAdjusting: Boolean(event.isSelfAdjusting),
  isActive: Boolean(event.isActive),
  repeatFrequency: "none",
  repeatCount: "1",
  ...mapScheduleToForm(event.selfAdjustingSchedule),
});

const buildPayload = (form) => ({
  title: form.title.trim(),
  description: (form.descriptionEn || form.description || form.descriptionDe).trim(),
  descriptionEn: form.descriptionEn.trim() || null,
  descriptionDe: form.descriptionDe.trim() || null,
  included: form.includedText
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean),
  includedEn: form.includedTextEn
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean),
  includedDe: form.includedTextDe
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean),
  locationId: form.locationId,
  startUtc: fromLocalInput(form.startLocal),
  endUtc: fromLocalInput(form.endLocal),
  capacity: Number.parseInt(form.capacity, 10) || 0,
  priceAmount: Number.parseFloat(form.priceAmount) || 0,
  equipmentTypeId: form.equipmentTypeId || null,
  equipmentTypeIds:
    Array.isArray(form.equipmentTypeIds) && form.equipmentTypeIds.length
      ? form.equipmentTypeIds
      : form.equipmentTypeId
        ? [form.equipmentTypeId]
        : [],
  equipmentQuantityPerAttendee:
    Number.parseInt(form.equipmentQuantityPerAttendee, 10) || 1,
  currency: "EUR",
  imageUrl: form.imageUrl.trim() || null,
  eventLocationName: form.eventLocationName.trim() || null,
  eventLocationAddress: form.eventLocationAddress.trim() || null,
  mapUrl: form.mapUrl.trim() || null,
  isSelfAdjusting: Boolean(form.isSelfAdjusting),
  selfAdjustingSchedule: buildSelfAdjustingSchedule(form),
  isActive: Boolean(form.isActive),
});

const shiftLocalDateTime = (value, frequency, index) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || index <= 0 || frequency === "none") {
    return value;
  }

  if (frequency === "weekly") {
    date.setDate(date.getDate() + index * 7);
  } else if (frequency === "biweekly") {
    date.setDate(date.getDate() + index * 14);
  } else if (frequency === "monthly") {
    date.setMonth(date.getMonth() + index);
  }

  return toLocalInput(date);
};

const buildRecurringPayloads = (form) => {
  const frequency = form.repeatFrequency || "none";
  if (form.isSelfAdjusting) {
    return [buildPayload(form)];
  }
  const count =
    frequency === "none"
      ? 1
      : Math.max(1, Math.min(52, Number.parseInt(form.repeatCount, 10) || 1));

  return Array.from({ length: count }, (_, index) =>
    buildPayload({
      ...form,
      startLocal: shiftLocalDateTime(form.startLocal, frequency, index),
      endLocal: shiftLocalDateTime(form.endLocal, frequency, index),
    })
  );
};

const occurrenceScheduleChanged = (event, payload) =>
  Boolean(
    event &&
      (new Date(event.startUtc).getTime() !== new Date(payload.startUtc).getTime() ||
        new Date(event.endUtc).getTime() !== new Date(payload.endUtc).getTime() ||
        event.locationId !== payload.locationId)
  );

const buildTemplatePayload = (form, name) => {
  const startMs = new Date(form.startLocal).getTime();
  const endMs = new Date(form.endLocal).getTime();
  const durationMinutes =
    Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs
      ? Math.round((endMs - startMs) / 60000)
      : 120;

  return {
    name: name.trim(),
    title: form.title.trim(),
    description: (form.descriptionEn || form.description || form.descriptionDe).trim(),
    descriptionEn: form.descriptionEn.trim() || null,
    descriptionDe: form.descriptionDe.trim() || null,
    included: form.includedText
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean),
    includedEn: form.includedTextEn
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean),
    includedDe: form.includedTextDe
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean),
    capacity: Number.parseInt(form.capacity, 10) || 0,
    priceAmount: Number.parseFloat(form.priceAmount) || 0,
    equipmentTypeId: form.equipmentTypeId || null,
    equipmentTypeIds:
      Array.isArray(form.equipmentTypeIds) && form.equipmentTypeIds.length
        ? form.equipmentTypeIds
        : form.equipmentTypeId
          ? [form.equipmentTypeId]
          : [],
    equipmentQuantityPerAttendee:
      Number.parseInt(form.equipmentQuantityPerAttendee, 10) || 1,
    imageUrl: form.imageUrl.trim() || null,
    eventLocationName: form.eventLocationName.trim() || null,
    eventLocationAddress: form.eventLocationAddress.trim() || null,
    mapUrl: form.mapUrl.trim() || null,
    durationMinutes,
    isSelfAdjusting: Boolean(form.isSelfAdjusting),
    selfAdjustingSchedule: buildSelfAdjustingSchedule(form),
    isActive: true,
  };
};

const useAdminEvents = ({ locations, equipmentSnapshot = [], showFeedback }) => {
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState("");
  const [eventFormOpen, setEventFormOpen] = useState(false);
  const [eventEditingId, setEventEditingId] = useState(null);
  const [eventForm, setEventForm] = useState(() =>
    createEmptyForm(locations, equipmentSnapshot)
  );
  const [eventSaving, setEventSaving] = useState(false);
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [eventActionId, setEventActionId] = useState(null);
  const [eventTemplates, setEventTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [attendeeForms, setAttendeeForms] = useState({});
  const [attendeeActionId, setAttendeeActionId] = useState(null);
  const [attendeeEditingId, setAttendeeEditingId] = useState(null);
  const [attendeeEditForm, setAttendeeEditForm] = useState(null);

  const sortedEvents = useMemo(
    () => {
      const now = Date.now();
      const upcoming = events
        .filter((event) => new Date(event.endUtc).getTime() >= now)
        .sort((a, b) => new Date(a.startUtc).getTime() - new Date(b.startUtc).getTime());
      const history = events
        .filter((event) => new Date(event.endUtc).getTime() < now)
        .sort((a, b) => new Date(b.startUtc).getTime() - new Date(a.startUtc).getTime());
      return [...upcoming, ...history];
    },
    [events]
  );

  const refreshEvents = async ({ silent = false } = {}) => {
    if (!silent) setEventsLoading(true);
    setEventsError("");
    try {
      const payload = await getAdminEvents();
      setEvents(Array.isArray(payload) ? payload : []);
    } catch (error) {
      setEventsError(error?.message || "Failed to load events.");
      setEvents([]);
    } finally {
      if (!silent) setEventsLoading(false);
    }
  };

  const refreshTemplates = async ({ silent = false } = {}) => {
    if (!silent) setTemplatesLoading(true);
    try {
      const payload = await getAdminEventTemplates();
      setEventTemplates(Array.isArray(payload) ? payload : []);
    } catch (error) {
      showFeedback(error?.message || "Failed to load event templates.", "warning");
      setEventTemplates([]);
    } finally {
      if (!silent) setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    refreshEvents();
    refreshTemplates();
    // The refresh functions intentionally use current component state only on demand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (eventEditingId) return;
    setEventForm((prev) => {
      const locationId = prev.locationId || locations[0]?.id || "";
      const normalized = normalizeEquipmentSelection({
        locations,
        equipmentSnapshot,
        locationId,
        equipmentTypeId: prev.equipmentTypeId,
        equipmentTypeIds: prev.equipmentTypeIds,
      });

      if (
        prev.locationId === locationId &&
        prev.equipmentTypeId === normalized.equipmentTypeId &&
        JSON.stringify(prev.equipmentTypeIds || []) === JSON.stringify(normalized.equipmentTypeIds)
      ) {
        return prev;
      }

      return {
        ...prev,
        locationId,
        ...normalized,
      };
    });
  }, [equipmentSnapshot, eventEditingId, eventForm.equipmentTypeId, eventForm.locationId, locations]);

  const openEventForm = () => {
    setEventEditingId(null);
    setEventForm(createEmptyForm(locations, equipmentSnapshot));
    setEventFormOpen(true);
  };

  const handleEventEdit = (event) => {
    setEventEditingId(event.id);
    setEventForm(mapEventToForm(event));
    setEventFormOpen(true);
  };

  const handleEventDuplicate = (event) => {
    const copiedForm = mapEventToForm(event);
    setEventEditingId(null);
    setEventForm({
      ...copiedForm,
      startLocal: shiftLocalDateTime(copiedForm.startLocal, "weekly", 1),
      endLocal: shiftLocalDateTime(copiedForm.endLocal, "weekly", 1),
      repeatFrequency: "none",
      repeatCount: "1",
    });
    setEventFormOpen(true);
  };

  const handleEventFormChange = (field, value) => {
    setEventForm((prev) => {
      if (field === "locationId") {
        const normalized = normalizeEquipmentSelection({
          locations,
          equipmentSnapshot,
          locationId: value,
          equipmentTypeId: prev.equipmentTypeId,
          equipmentTypeIds: prev.equipmentTypeIds,
        });
        return { ...prev, locationId: value, ...normalized };
      }

      if (field === "equipmentTypeId") {
        const locationEquipmentIds = getLocationEquipmentIds(locations, prev.locationId);
        const currentIds = Array.isArray(prev.equipmentTypeIds)
          ? prev.equipmentTypeIds.filter((id) => locationEquipmentIds.has(id))
          : [];
        return {
          ...prev,
          equipmentTypeId: value,
          equipmentTypeIds: value && !currentIds.includes(value) ? [value, ...currentIds] : currentIds,
        };
      }

      if (field === "equipmentTypeIds") {
        const locationEquipmentIds = getLocationEquipmentIds(locations, prev.locationId);
        const nextIds = Array.isArray(value)
          ? value.filter((id) => locationEquipmentIds.has(id))
          : [];
        if (prev.equipmentTypeId && locationEquipmentIds.has(prev.equipmentTypeId)) {
          nextIds.push(prev.equipmentTypeId);
        }
        return { ...prev, equipmentTypeIds: [...new Set(nextIds)] };
      }

      return { ...prev, [field]: value };
    });
  };

  const handleApplyTemplate = () => {
    const template = eventTemplates.find((item) => item.id === selectedTemplateId);
    if (!template) {
      showFeedback("Choose a template first.", "warning");
      return;
    }

    const startDate = new Date(eventForm.startLocal);
    const safeStart = Number.isNaN(startDate.getTime()) ? new Date() : startDate;
    const endDate = new Date(safeStart);
    endDate.setMinutes(endDate.getMinutes() + (template.durationMinutes || 120));

    setEventForm((prev) => ({
      ...prev,
      title: template.title || prev.title,
      description: template.description || prev.description,
      descriptionEn: template.descriptionEn || template.description || prev.descriptionEn,
      descriptionDe: template.descriptionDe || prev.descriptionDe,
      includedText: Array.isArray(template.included) ? template.included.join("\n") : "",
      includedTextEn:
        Array.isArray(template.includedEn) && template.includedEn.length
          ? template.includedEn.join("\n")
          : Array.isArray(template.included)
            ? template.included.join("\n")
            : "",
      includedTextDe: Array.isArray(template.includedDe) ? template.includedDe.join("\n") : "",
      capacity: String(template.capacity ?? prev.capacity),
      priceAmount: String(template.priceAmount ?? prev.priceAmount),
      equipmentTypeId: template.equipmentTypeId || prev.equipmentTypeId,
      equipmentTypeIds:
        Array.isArray(template.equipmentOptions) && template.equipmentOptions.length
          ? template.equipmentOptions.map((item) => item.equipmentTypeId).filter(Boolean)
          : template.equipmentTypeId
            ? [template.equipmentTypeId]
            : prev.equipmentTypeIds,
      equipmentQuantityPerAttendee: String(
        template.equipmentQuantityPerAttendee ?? prev.equipmentQuantityPerAttendee
      ),
      imageUrl: template.imageUrl || prev.imageUrl,
      eventLocationName: template.eventLocationName || prev.eventLocationName,
      eventLocationAddress: template.eventLocationAddress || prev.eventLocationAddress,
      mapUrl: template.mapUrl || prev.mapUrl,
      isSelfAdjusting: Boolean(template.isSelfAdjusting),
      ...mapScheduleToForm(template.selfAdjustingSchedule),
      endLocal: toLocalInput(endDate),
    }));
    showFeedback("Template applied. Adjust anything before publishing.", "info");
  };

  const handleSaveTemplateFromForm = async () => {
    const name = window.prompt("Template name", eventForm.title || "Event template");
    if (!name?.trim()) return;

    try {
      const saved = await createAdminEventTemplate(buildTemplatePayload(eventForm, name));
      setEventTemplates((prev) => [...prev, saved]);
      setSelectedTemplateId(saved.id);
      showFeedback("Template saved.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to save template.", "warning");
    }
  };

  const handleUpdateSelectedTemplate = async () => {
    const template = eventTemplates.find((item) => item.id === selectedTemplateId);
    if (!template) {
      showFeedback("Choose a template first.", "warning");
      return;
    }

    try {
      const saved = await updateAdminEventTemplate(
        template.id,
        buildTemplatePayload(eventForm, template.name)
      );
      setEventTemplates((prev) =>
        prev.map((item) => (item.id === saved.id ? saved : item))
      );
      showFeedback("Template updated.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to update template.", "warning");
    }
  };

  const handleDeleteSelectedTemplate = async () => {
    const template = eventTemplates.find((item) => item.id === selectedTemplateId);
    if (!template) {
      showFeedback("Choose a template first.", "warning");
      return;
    }
    if (!window.confirm(`Delete template ${template.name}?`)) return;

    try {
      await deleteAdminEventTemplate(template.id);
      setEventTemplates((prev) => prev.filter((item) => item.id !== template.id));
      setSelectedTemplateId("");
      showFeedback("Template deleted.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to delete template.", "warning");
    }
  };

  const handleEventImageUpload = async (file) => {
    if (!file) return;
    setImageUploading(true);
    try {
      const payload = await uploadAdminEventImage(file);
      if (!payload?.imageUrl) {
        throw new Error("Upload did not return an image URL.");
      }
      setEventForm((prev) => ({ ...prev, imageUrl: payload.imageUrl }));
      showFeedback("Event image uploaded.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to upload image.", "warning");
    } finally {
      setImageUploading(false);
    }
  };

  const handleEventSave = async (submitEvent) => {
    submitEvent.preventDefault();
    setEventSaving(true);
    try {
      const normalized = normalizeEquipmentSelection({
        locations,
        equipmentSnapshot,
        locationId: eventForm.locationId,
        equipmentTypeId: eventForm.equipmentTypeId,
        equipmentTypeIds: eventForm.equipmentTypeIds,
      });
      if (!normalized.equipmentTypeId) {
        throw new Error("Choose an inventory item that is available at this location.");
      }
      const payloads = buildRecurringPayloads({ ...eventForm, ...normalized });
      const editedEvent = eventEditingId
        ? events.find((item) => item.id === eventEditingId)
        : null;
      const preserveBookedOccurrence =
        Boolean(editedEvent?.bookedCount) && occurrenceScheduleChanged(editedEvent, payloads[0]);
      const savedEvents = eventEditingId && !preserveBookedOccurrence
        ? [
            await updateAdminEvent(eventEditingId, payloads[0]),
            ...(payloads.length > 1
              ? await Promise.all(payloads.slice(1).map((payload) => createAdminEvent(payload)))
              : []),
          ]
        : await Promise.all(payloads.map((payload) => createAdminEvent(payload)));
      const saved = savedEvents[0];
      setEvents((prev) => {
        if (eventEditingId && !preserveBookedOccurrence) {
          return [
            ...prev.map((item) => (item.id === saved.id ? saved : item)),
            ...savedEvents.slice(1),
          ];
        }
        return [...prev, ...savedEvents];
      });
      setEventFormOpen(false);
      setEventEditingId(null);
      setEventForm(createEmptyForm(locations, equipmentSnapshot));
      showFeedback(
        preserveBookedOccurrence
          ? savedEvents.length === 1
            ? "New occurrence created. The original date and its attendees remain in history."
            : `${savedEvents.length} new occurrences created. The original date and its attendees were preserved.`
          : eventEditingId
          ? savedEvents.length === 1
            ? "Event updated."
            : `Event updated and ${savedEvents.length - 1} recurring occurrences created.`
          : savedEvents.length === 1
            ? "Event created."
            : `${savedEvents.length} recurring events created.`,
        "success"
      );
    } catch (error) {
      showFeedback(error?.message || "Failed to save event.", "warning");
    } finally {
      setEventSaving(false);
    }
  };

  const handleEventDelete = async (event) => {
    const confirmed = window.confirm(
      `Delete ${event.title}? Events with attendees will be hidden instead of removed.`
    );
    if (!confirmed) return;
    setEventActionId(event.id);
    try {
      await deleteAdminEvent(event.id);
      await refreshEvents({ silent: true });
      showFeedback("Event deleted.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to delete event.", "warning");
    } finally {
      setEventActionId(null);
    }
  };

  const handleAttendeeFormChange = (eventId, field, value) => {
    setAttendeeForms((prev) => ({
      ...prev,
      [eventId]: {
        contactName: "",
        contactEmail: "",
        contactPhone: "",
        priceAmount: "",
        isPaid: false,
        ...(prev[eventId] ?? {}),
        [field]: value,
      },
    }));
  };

  const handleAddAttendee = async (submitEvent, eventId) => {
    submitEvent.preventDefault();
    const form = attendeeForms[eventId] ?? {};
    const rawPrice = `${form.priceAmount ?? ""}`.trim();
    const priceAmount = rawPrice === "" ? null : Number.parseFloat(rawPrice);
    if (priceAmount !== null && (!Number.isFinite(priceAmount) || priceAmount < 0)) {
      showFeedback("Enter a valid attendee price.", "warning");
      return;
    }
    setAttendeeActionId(eventId);
    try {
      const updated = await addAdminEventAttendee(eventId, {
        contactName: form.contactName || null,
        contactEmail: form.contactEmail || "",
        contactPhone: form.contactPhone || "",
        priceAmount,
        isPaid: Boolean(form.isPaid),
      });
      setEvents((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setAttendeeForms((prev) => ({
        ...prev,
        [eventId]: {
          contactName: "",
          contactEmail: "",
          contactPhone: "",
          priceAmount: "",
          isPaid: false,
        },
      }));
      showFeedback("Person added to event.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to add person.", "warning");
    } finally {
      setAttendeeActionId(null);
    }
  };

  const handleRemoveAttendee = async (eventId, attendee) => {
    const onlineBookingWarning = attendee.isManual
      ? ""
      : " This cancels the website booking but does not issue a refund. Refunds remain available in Bookings.";
    if (
      !window.confirm(
        `Remove ${attendee.contactName || "this attendee"} from this event?${onlineBookingWarning}`
      )
    ) return;
    setAttendeeActionId(attendee.bookingId);
    try {
      const updated = await removeAdminEventAttendee(eventId, attendee.bookingId);
      setEvents((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      showFeedback("Person removed from event.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to remove person.", "warning");
    } finally {
      setAttendeeActionId(null);
    }
  };

  const handleAttendeeEdit = (eventItem, attendee) => {
    setAttendeeEditingId(attendee.bookingId);
    setAttendeeEditForm({
      contactName: attendee.contactName || "",
      contactEmail: attendee.contactEmail || "",
      contactPhone: attendee.contactPhone || "",
      priceAmount: String(attendee.priceAmount ?? eventItem.priceAmount ?? ""),
      isPaid: attendee.paymentStatus === "Succeeded",
    });
  };

  const handleAttendeeEditChange = (field, value) => {
    setAttendeeEditForm((prev) => ({
      ...(prev || {}),
      [field]: value,
    }));
  };

  const handleAttendeeEditCancel = () => {
    setAttendeeEditingId(null);
    setAttendeeEditForm(null);
  };

  const handleAttendeeUpdate = async (submitEvent, eventId, bookingId) => {
    submitEvent.preventDefault();
    const form = attendeeEditForm || {};
    const rawPrice = `${form.priceAmount ?? ""}`.trim();
    const priceAmount = rawPrice === "" ? null : Number.parseFloat(rawPrice);
    if (priceAmount !== null && (!Number.isFinite(priceAmount) || priceAmount < 0)) {
      showFeedback("Enter a valid attendee price.", "warning");
      return;
    }

    setAttendeeActionId(bookingId);
    try {
      const updated = await updateAdminEventAttendee(eventId, bookingId, {
        contactName: form.contactName || null,
        contactEmail: form.contactEmail || "",
        contactPhone: form.contactPhone || "",
        priceAmount,
        isPaid: Boolean(form.isPaid),
      });
      setEvents((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      handleAttendeeEditCancel();
      showFeedback("Attendee updated.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to update attendee.", "warning");
    } finally {
      setAttendeeActionId(null);
    }
  };

  return {
    events: sortedEvents,
    eventsLoading,
    eventsError,
    eventFormOpen,
    setEventFormOpen,
    eventEditingId,
    setEventEditingId,
    eventForm,
    setEventForm,
    eventSaving,
    expandedEventId,
    setExpandedEventId,
    eventActionId,
    eventTemplates,
    templatesLoading,
    selectedTemplateId,
    setSelectedTemplateId,
    imageUploading,
    attendeeForms,
    attendeeActionId,
    attendeeEditingId,
    attendeeEditForm,
    openEventForm,
    handleEventEdit,
    handleEventDuplicate,
    handleEventFormChange,
    handleApplyTemplate,
    handleSaveTemplateFromForm,
    handleUpdateSelectedTemplate,
    handleDeleteSelectedTemplate,
    handleEventImageUpload,
    handleEventSave,
    handleEventDelete,
    handleAttendeeFormChange,
    handleAddAttendee,
    handleRemoveAttendee,
    handleAttendeeEdit,
    handleAttendeeEditChange,
    handleAttendeeEditCancel,
    handleAttendeeUpdate,
    refreshEvents,
  };
};

export default useAdminEvents;
